import { DotIcon } from "@primer/octicons-react";
import Prism from "prismjs";
import { useEffect } from "react";
import { Header, Share, Sidebar } from "../../components";

const LazyLoad = () => {
  useEffect(() => {
    Prism.highlightAll();
    document.title = "PWA Fire - LazyLoad API";
  }, []);
  return (
    <article>
      <Header />
      <section className="docs-flex">
        <Sidebar />
        <section className="docs-flex-right fade-in docs post">
          <div className="date">
            <span>Last updated -: Apr 07 2025</span>
            <Share
              data={{
                url: "https://docs.pwafire.org/link/lazyload",
                title: "LazyLoad API",
                text: "Lazy load images and animate elements when they enter the viewport."
              }}
            />
          </div>
          <h3>Lazy Loading for your PWA</h3>
          <p className="quote">
            <b>LazyLoad API</b> makes it easy to defer loading images and
            content until they're needed, improving performance and user
            experience for your PWA by reducing initial page load time and
            saving bandwidth.
          </p>

          <h4>Lazy Load Images</h4>
          <p>
            <DotIcon />
            Load images when they scroll into view
          </p>
          <pre id="copy-code">
            <code className="language-javascript">{`  // Lazy load images with data-src attribute
  const result = await loadImage('.lazy-image', {
    src: 'data-src',
    placeholder: 'placeholder.jpg'
  });

  // Check if lazy loading was successful
  if (result.ok) {
    console.log(result.message);
  }`}</code>
          </pre>

          <h4>Lazy Load Background Images</h4>
          <p>
            <DotIcon />
            Load background images when elements scroll into view
          </p>
          <pre id="copy-code">
            <code className="language-javascript">{`  // Lazy load background images
  await loadBackground('.hero-section', {
    background: 'data-background',
    placeholder: '#f0f0f0'  // Light gray placeholder
  });`}</code>
          </pre>

          <h4>Animate Elements on Scroll</h4>
          <p>
            <DotIcon />
            Reveal elements with animation when they enter the viewport
          </p>
          <pre id="copy-code">
            <code className="language-javascript">{`  // Animate elements when they scroll into view
  await loadOnScroll('.fade-in-section', {
    style: 'fade',  // Options: fade, slide, zoom, none
    delay: 100      // 100ms staggered delay between elements
  });`}</code>
          </pre>

          <h4>Initialize All Lazy Loading</h4>
          <p>
            <DotIcon />
            Set up all lazy loading types with one call
          </p>
          <pre id="copy-code">
            <code className="language-javascript">{`  // Initialize all lazy loading
  await lazyLoad({
    images: '.lazy-image',
    backgrounds: '.lazy-background',
    animations: '.reveal-on-scroll',
    style: 'slide'
  });`}</code>
          </pre>

          <h4>HTML Structure</h4>
          <p>
            <DotIcon />
            Set up your HTML with the appropriate data attributes
          </p>
          <pre id="copy-code">
            <code className="language-html">{`  <!-- Lazy loaded image -->
  <img class="lazy-image" data-src="image.jpg" alt="Description">

  <!-- Background image -->
  <div class="lazy-background" data-background="background.jpg"></div>

  <!-- Element that reveals on scroll -->
  <div class="reveal-on-scroll">This content will animate in</div>`}</code>
          </pre>

          <h4>Browser Support</h4>
          <p>
            The LazyLoad API is supported in all modern browsers. It
            automatically checks for support and returns an appropriate status
            that you can use to implement fallbacks for older browsers.
          </p>

          <h4>Performance Benefits</h4>
          <p>Implementing lazy loading can significantly improve:</p>
          <ul>
            <li>Initial page load time by deferring off-screen images</li>
            <li>Bandwidth usage by only loading resources when needed</li>
            <li>Battery life on mobile devices by reducing processing</li>
            <li>User experience with smooth loading and animations</li>
          </ul>
        </section>
      </section>
    </article>
  );
};

export default LazyLoad;
